import { FormField, InputSelectField } from '@tempmee/react';
import { forwardRef, memo, type Ref, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { getUserEducation } from '~/actions';
import { UserProfession } from '~/enums/UserProfession';
import { getShouldShowFrontDeskSpecialty } from '~/growthbook';
import { useAppSelector } from '~/hooks/useAppSelector';
import { filterObjectKeys } from '~/utils/Object';

interface IPreferredSpecialtyFieldProps {
  value?: string;
  onChange?: (value: string) => void;
  professionType: string;
  hasError?: boolean;
}

const Component = (
  { value, onChange, professionType, hasError }: IPreferredSpecialtyFieldProps,
  ref: Ref<HTMLDivElement>,
) => {
  const dispatch = useDispatch();

  const userEducation = useAppSelector((state) => state.user.userEducation);

  useEffect(() => {
    if (!professionType) return;
    const code = UserProfession.getCode(professionType) as string | undefined;
    if (!code) return;
    const professionCode = UserProfession.getNormalizedCode(code);
    dispatch(getUserEducation({ profession: professionCode }));
  }, [dispatch, professionType]);

  const normalizedCode = useMemo(() => {
    if (!professionType) return undefined;
    const code = UserProfession.getCode(professionType) as string | undefined;
    if (!code) return undefined;
    return UserProfession.getNormalizedCode(code) as string;
  }, [professionType]);

  const specialtiesAndProcedures = normalizedCode
    ? (userEducation as any)?.[normalizedCode]?.specialtiesAndProcedures
    : undefined;

  const isLoadingSpecialties = !!professionType && !specialtiesAndProcedures;

  const specialties = useMemo(() => {
    if (!specialtiesAndProcedures) return [];

    const filteredSpecialties = filterObjectKeys(specialtiesAndProcedures, 'specialty');
    const keys = Object.keys(filteredSpecialties);

    const showFrontDesk = getShouldShowFrontDeskSpecialty();

    return showFrontDesk ? keys : keys.filter((key) => key !== 'specialty_Front Desk');
  }, [specialtiesAndProcedures]);

  useEffect(() => {
    if (value || specialties.length === 0) return;
    const generalDentistry = specialties.find((s) => s === 'specialty_General Dentistry');
    if (generalDentistry) {
      onChange?.(generalDentistry);
    }
  }, [specialties, value, onChange]);

  const options = useMemo(
    () => specialties.map((s) => ({ label: s.split('_')[1], value: s })),
    [specialties],
  );

  return (
    <div ref={ref} data-testid="preferred-specialty-field" data-cy="preferred-specialty-field">
      <FormField label="Preferred Specialty">
        <InputSelectField<string>
          options={options}
          value={value}
          onChange={onChange}
          placeholder="Select Specialty"
          isError={Boolean(hasError)}
          isLoading={isLoadingSpecialties}
          disabled={!professionType}
        />
      </FormField>
    </div>
  );
};

export const PreferredSpecialtyField = memo(forwardRef(Component));
